import type { ChecklistTemplate } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';
import { WeaponType } from '@/types/season';

const TEMPLATE_ID = 'builtin-bow-season-midwest';
const BASE_DATE = '2026-01-01T00:00:00.000Z';

const T = {
  postSeasonScouting: 'bow-midwest-001',
  markBedding: 'bow-midwest-002',
  shedHunting: 'bow-midwest-003',
  frostSeedClover: 'bow-midwest-004',
  tsiHingeCuts: 'bow-midwest-005',
  orderSeed: 'bow-midwest-006',
  bowTuneUp: 'bow-midwest-007',
  broadheadSharpen: 'bow-midwest-008',
  shootingPractice: 'bow-midwest-009',
  trailCamSummer: 'bow-midwest-010',
  warmSeasonPlanting: 'bow-midwest-011',
  mockScrapeInstall: 'bow-midwest-012',
  waterholeMainten: 'bow-midwest-013',
  treestandHanging: 'bow-midwest-014',
  lifelines: 'bow-midwest-015',
  shootingLaneTrim: 'bow-midwest-016',
  accessRoutes: 'bow-midwest-017',
  fallBrassicas: 'bow-midwest-018',
  fallCerealGrains: 'bow-midwest-019',
  cameraReposition: 'bow-midwest-020',
  scentControl: 'bow-midwest-021',
  licensePurchase: 'bow-midwest-022',
  preSeasonSit: 'bow-midwest-023',
  finalBowPractice: 'bow-midwest-024',
  finalCamPull: 'bow-midwest-025',
  packGear: 'bow-midwest-026',
  earlySeasonStrategy: 'bow-midwest-027',
  postHuntEval: 'bow-midwest-028',
  midSeasonCamPull: 'bow-midwest-029',
} as const;

export const BOW_SEASON_TEMPLATE: ChecklistTemplate = {
  id: TEMPLATE_ID,
  name: 'Whitetail Bow Season Prep — Midwest',
  description:
    'Pre-season preparation for archery deer season. Covers stand prep, scouting, food plot timing, gear tuning, and early-season strategy. Designed for Midwest landowners.',
  weaponType: WeaponType.Bow,
  region: 'midwest',
  isBuiltIn: true,
  version: 1,
  createdAt: BASE_DATE,
  updatedAt: BASE_DATE,
  tasks: [
    // ─── January: Post-season scouting ───────────────────────────────────────
    {
      id: T.postSeasonScouting,
      templateId: TEMPLATE_ID,
      title: 'Post-season scouting: bedding and travel routes',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['post_season'],
      sortOrder: 0,
      notes: 'Sign fresh from gun season. Map bedding areas and travel corridors while leaf-free.',
    },
    {
      id: T.markBedding,
      templateId: TEMPLATE_ID,
      title: 'Mark bedding areas and corridors (flagging tape)',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 25,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.postSeasonScouting],
      predefinedActions: [],
      sortOrder: 1,
      notes: 'Flag bedding areas and travel corridors found during scouting. Reference points for stand placement.',
    },
    // ─── February: Habitat work and spring planting setup ────────────────────
    {
      id: T.shedHunting,
      templateId: TEMPLATE_ID,
      title: 'Shed hunting',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['shed_hunt'],
      sortOrder: 2,
      notes: 'Feb-Mar. Collect sheds for herd inventory. Note where bucks survived and bedded.',
    },
    {
      id: T.frostSeedClover,
      templateId: TEMPLATE_ID,
      title: 'Frost-seed clover (soil temp 32-40°F)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 28,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['frost_seed'],
      sortOrder: 3,
      notes: 'Broadcast clover onto frozen/thawing ground. Freeze-thaw cycle pulls seed into soil. Late Feb - early Mar in Zone 8.',
    },
    {
      id: T.tsiHingeCuts,
      templateId: TEMPLATE_ID,
      title: 'TSI / hinge cuts / bedding area improvement',
      description: '',
      category: TaskCategory.HabitatManagement,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['cut'],
      sortOrder: 4,
      notes: 'Winter is lowest-impact time. Execute TSI, hinge cuts, bedding area creation. Feb-Mar window.',
    },
    // ─── March: Seed order ────────────────────────────────────────────────────
    {
      id: T.orderSeed,
      templateId: TEMPLATE_ID,
      title: 'Order food plot seed for fall plots',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 3,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 5,
      notes: 'Order early to avoid shortages. Brassica, cereal grain, and clover mixes for fall planting.',
    },
    // ─── Opener-relative: -120 days (bow gear) ───────────────────────────────
    {
      id: T.bowTuneUp,
      templateId: TEMPLATE_ID,
      title: 'Bow tune-up: full service or DIY check',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -120,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['tune'],
      sortOrder: 6,
      notes: 'Inspect cables, strings, cams, and timing. Send to shop or DIY. Allow time before practice.',
    },
    {
      id: T.broadheadSharpen,
      templateId: TEMPLATE_ID,
      title: 'Broadhead sharpening and replacement',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -120,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [T.bowTuneUp],
      predefinedActions: ['sharpen'],
      sortOrder: 7,
      notes: 'Fixed blade: sharpen to shaving sharp. Mechanical: replace blades. Test flight with practice heads.',
    },
    {
      id: T.shootingPractice,
      templateId: TEMPLATE_ID,
      title: 'Begin shooting practice regimen',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -120,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [T.bowTuneUp],
      predefinedActions: [],
      sortOrder: 8,
      notes: '3D course or backyard range. Build to 40+ yards. Shoot from elevated platform. Shoot until muscle memory is solid.',
    },
    // ─── Opener-relative: -90 days ───────────────────────────────────────────
    {
      id: T.trailCamSummer,
      templateId: TEMPLATE_ID,
      title: 'Trail cam summer deployment: velvet inventory',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['deploy'],
      sortOrder: 9,
      notes: 'Fresh batteries, new cards. Deploy over mineral sites and water sources for summer velvet buck inventory.',
    },
    // ─── May: Warm-season food plot ───────────────────────────────────────────
    {
      id: T.warmSeasonPlanting,
      templateId: TEMPLATE_ID,
      title: 'Warm-season food plot planting (soybeans, clover)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 5,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 30,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['plant'],
      sortOrder: 10,
      notes: 'Early-season draw. Plant when soil temp reaches 60-65°F. Soybeans and clover for summer attraction.',
    },
    {
      id: T.mockScrapeInstall,
      templateId: TEMPLATE_ID,
      title: 'Mock scrape installation',
      description: '',
      category: TaskCategory.MockScrapes,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['create'],
      sortOrder: 11,
      notes: 'Licking branch + bare earth at edges of cover. Place near travel corridors. Allow 60+ days for bucks to adopt.',
    },
    // ─── Opener-relative: -75 days ───────────────────────────────────────────
    {
      id: T.waterholeMainten,
      templateId: TEMPLATE_ID,
      title: 'Waterhole maintenance / creation',
      description: '',
      category: TaskCategory.WaterholeManagement,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -75,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['maintain'],
      sortOrder: 12,
      notes: 'Summer drought makes water a major draw. Clean and refresh waterholes. Allow deer time to pattern on them.',
    },
    {
      id: T.treestandHanging,
      templateId: TEMPLATE_ID,
      title: 'Treestand hanging / annual safety inspection',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -75,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['hang', 'inspect'],
      sortOrder: 13,
      notes: 'Hang stands early — 75+ days gives deer time to settle. Inspect all straps, bolts, chains, and cables.',
    },
    {
      id: T.lifelines,
      templateId: TEMPLATE_ID,
      title: 'Install/inspect lifelines and safety systems',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -75,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['install_lifeline'],
      sortOrder: 14,
      notes: 'Lifeline systems for every stand. Non-negotiable safety item.',
    },
    // ─── Opener-relative: -60 days ───────────────────────────────────────────
    {
      id: T.shootingLaneTrim,
      templateId: TEMPLATE_ID,
      title: 'Shooting lane trimming',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.treestandHanging],
      predefinedActions: ['trim_lanes'],
      sortOrder: 15,
      notes: 'Conservative cuts — maintain cover. Trim only what is necessary after stands are hung so you know exact shooting angles.',
    },
    {
      id: T.accessRoutes,
      templateId: TEMPLATE_ID,
      title: 'Clear and mark access routes (entry/exit paths)',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.treestandHanging],
      predefinedActions: [],
      sortOrder: 16,
      notes: 'Scent-free entry and exit paths. Avoid bedding areas. Clear noisy branches and obstacles.',
    },
    // ─── August: Fall food plots ──────────────────────────────────────────────
    {
      id: T.fallBrassicas,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: brassicas (Aug 1-15, Zone 8)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['till', 'plant'],
      sortOrder: 17,
      notes: 'Brassica mix for Zone 8 (WI). Adjust +/- 2 weeks by zone. Post-frost sweetening is the primary late-season draw.',
    },
    {
      id: T.fallCerealGrains,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: cereal grains (Aug 15 - Sep 1)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['plant'],
      sortOrder: 18,
      notes: 'Oats, rye, wheat. Quick germination provides early-season green browse within 2 weeks of planting.',
    },
    // ─── Opener-relative: -45 days ───────────────────────────────────────────
    {
      id: T.cameraReposition,
      templateId: TEMPLATE_ID,
      title: 'Trail cam repositioning: transition to travel corridors',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['reposition'],
      sortOrder: 19,
      notes: 'Move off food to trails, funnels, and pinch points. Transition from summer velvet intel to pre-rut patterns.',
    },
    // ─── Opener-relative: -30 days ───────────────────────────────────────────
    {
      id: T.scentControl,
      templateId: TEMPLATE_ID,
      title: 'Scent control gear prep: wash, store, ozone',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -30,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['clean'],
      sortOrder: 20,
      notes: 'Scent-free detergent on all clothing. Store in sealed scent-free bags. Treat boots. Archery requires close encounters — scent control is critical.',
    },
    {
      id: T.licensePurchase,
      templateId: TEMPLATE_ID,
      title: 'Purchase licenses and tags',
      description: '',
      category: TaskCategory.RegulatoryAdmin,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -30,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['purchase'],
      sortOrder: 21,
      notes: 'Buy archery license, tags, and any required permits. Check for regulation changes from prior year.',
    },
    // ─── Opener-relative: -21 days ───────────────────────────────────────────
    {
      id: T.preSeasonSit,
      templateId: TEMPLATE_ID,
      title: 'Scouting: pre-season sit — observe without hunting',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -21,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['pre_season'],
      sortOrder: 22,
      notes: 'Glass from distance or sit without hunting. Confirm buck patterns before opener without burning the stand.',
    },
    // ─── Opener-relative: -14 days ───────────────────────────────────────────
    {
      id: T.finalBowPractice,
      templateId: TEMPLATE_ID,
      title: 'Final bow practice: realistic scenarios',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -14,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 23,
      notes: 'Elevated shots, odd angles, cold muscles, full camo. Simulate actual hunting conditions before season.',
    },
    // ─── Opener-relative: -7 days ─────────────────────────────────────────────
    {
      id: T.finalCamPull,
      templateId: TEMPLATE_ID,
      title: 'Check trail cams — final pull before opener',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -7,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['pull_card'],
      sortOrder: 24,
      notes: 'Last intel pull before season. Confirm buck patterns, timing, and stand selection.',
    },
    // ─── Opener-relative: -3 days ─────────────────────────────────────────────
    {
      id: T.packGear,
      templateId: TEMPLATE_ID,
      title: 'Pack hunting gear (day pack, release, rangefinder)',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -3,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['pack'],
      sortOrder: 25,
      notes: 'Lay out all gear. Check batteries — rangefinder, headlamp, phone. Confirm release, safety harness, and license.',
    },
    // ─── Opener day ───────────────────────────────────────────────────────────
    {
      id: T.earlySeasonStrategy,
      templateId: TEMPLATE_ID,
      title: 'Early-season strategy: target food-to-bed transitions',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 0,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 26,
      notes: 'Evening sits targeting food-to-bed transitions. Warm weather limits deer movement to short windows near dark.',
    },
    // ─── Post-opener ──────────────────────────────────────────────────────────
    {
      id: T.postHuntEval,
      templateId: TEMPLATE_ID,
      title: 'Post-hunt evaluation: first week review',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 7,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 27,
      notes: 'Review first week of sits. What patterns are holding? Adjust stand selection based on real observations.',
    },
    {
      id: T.midSeasonCamPull,
      templateId: TEMPLATE_ID,
      title: 'Trail cam mid-season pull and analysis',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: 21,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['pull_card', 'analyze'],
      sortOrder: 28,
      notes: 'Pattern shifts as foliage drops and deer transition to rut staging areas. Adjust stand locations accordingly.',
    },
  ],
};
